import React, { useEffect, useState } from "react";
import { MapPin, Search, Star, Trash2, BookmarkPlus } from "lucide-react";
import { toast } from "sonner";
import { Field, Input, Btn } from "@/components/kit";
import { api } from "@/lib/api";

// Reusable address-lookup component with a MANUAL fallback + a per-driver
// saved-address book (quick-pick). We deliberately do NOT fake a live postcode
// provider (UAT prompt #8); when a real provider is wired in, replace `onLookup`.
//
// value / onChange shape: { postcode, line1, line2, city, address_text }

function compose({ line1, line2, city, postcode }) {
  return [line1, line2, city, postcode].map((p) => (p || "").trim()).filter(Boolean).join(", ");
}

export function AddressLookup({ value, onChange, label = "Address", testIdPrefix = "address", saveable = true }) {
  const v = value || {};
  const [manual, setManual] = useState(!!(v.line1 || v.city));
  const [saved, setSaved] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!saveable) return;
    let m = true;
    api.driverAddresses().then((r) => { if (m) { setSaved(r.addresses || []); setLoaded(true); } }).catch(() => setLoaded(true));
    return () => { m = false; };
  }, [saveable]);

  const set = (patch) => {
    const next = { ...v, ...patch };
    next.address_text = compose(next);
    onChange(next);
  };

  const pick = (a) => {
    onChange({ postcode: a.postcode || "", line1: a.line1 || "", line2: a.line2 || "", city: a.city || "", address_text: a.address_text || compose(a) });
    setManual(!!(a.line1 || a.city));
  };

  const saveCurrent = async () => {
    const text = v.address_text || compose(v);
    if (!text) { toast.error("Enter an address first."); return; }
    try {
      const r = await api.driverAddressAdd({ label: v.city || v.postcode || "Saved address", postcode: v.postcode, line1: v.line1, line2: v.line2, city: v.city, address_text: text });
      setSaved((s) => [r.address, ...s]);
      toast.success("Address saved to your address book.");
    } catch (e) { toast.error(e.message || "Could not save address."); }
  };

  const remove = async (id) => {
    try { await api.driverAddressDelete(id); setSaved((s) => s.filter((x) => x.id !== id)); }
    catch (e) { toast.error(e.message || "Could not remove address."); }
  };

  return (
    <div className="space-y-3">
      {saveable && loaded && saved.length > 0 && (
        <div data-testid={`${testIdPrefix}-saved`}>
          <span className="mb-1.5 block vda-tick text-muted-foreground">Saved addresses</span>
          <div className="flex flex-wrap gap-2">
            {saved.map((a) => (
              <span key={a.id} className="inline-flex items-center gap-1 pl-2.5 pr-1 h-8 text-xs rounded-full border border-border bg-secondary/40">
                <button type="button" data-testid={`${testIdPrefix}-saved-pick-${a.id}`} onClick={() => pick(a)} className="inline-flex items-center gap-1 font-medium">
                  <Star className="h-3 w-3 text-[hsl(var(--vda-amber))]" /> {a.label || a.address_text}
                </button>
                <button type="button" data-testid={`${testIdPrefix}-saved-del-${a.id}`} onClick={() => remove(a.id)} className="p-1 text-muted-foreground hover:text-[hsl(var(--vda-red))]">
                  <Trash2 className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      <Field label={label}>
        <div className="flex gap-2">
          <Input
            data-testid={`${testIdPrefix}-postcode`}
            value={v.postcode || ""}
            onChange={(e) => set({ postcode: e.target.value.toUpperCase() })}
            placeholder="Postcode (e.g. LE11 5GH)"
            className="uppercase"
          />
          <Btn type="button" variant="outline" data-testid={`${testIdPrefix}-manual-toggle`}
            onClick={() => setManual((m) => !m)} className="flex-none px-3">
            {manual ? <MapPin className="h-4 w-4" /> : <Search className="h-4 w-4" />}
            {manual ? "Hide" : "Enter address"}
          </Btn>
        </div>
      </Field>

      {!manual && (
        <p className="text-xs text-muted-foreground -mt-1">
          Enter the postcode, then tap <span className="font-medium">Enter address</span> to add the full address.
        </p>
      )}

      {manual && (
        <div className="space-y-2 border-l-2 border-border pl-3">
          <Input data-testid={`${testIdPrefix}-line1`} value={v.line1 || ""} onChange={(e) => set({ line1: e.target.value })} placeholder="Address line 1" />
          <Input data-testid={`${testIdPrefix}-line2`} value={v.line2 || ""} onChange={(e) => set({ line2: e.target.value })} placeholder="Address line 2 (optional)" />
          <Input data-testid={`${testIdPrefix}-city`} value={v.city || ""} onChange={(e) => set({ city: e.target.value })} placeholder="Town / city" />
        </div>
      )}

      {saveable && (v.address_text || v.postcode || v.line1) && (
        <button type="button" data-testid={`${testIdPrefix}-save`} onClick={saveCurrent}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline">
          <BookmarkPlus className="h-3.5 w-3.5" /> Save this address for next time
        </button>
      )}
    </div>
  );
}
